import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  makeStyles,
  Tabs,
  Tab,
  Tooltip,
} from "@material-ui/core";
import {
  Settings,
  Close,
  CloudUpload,
  GetApp,
  DeleteOutline,
  HelpOutline,
  Fullscreen,
  FullscreenExit,
  LibraryBooks,
  Description,
  Refresh,
} from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import { getApiBaseUrl } from "../config";
import CustomizationSliders from "./CustomizationSliders";
import GenerationModeSelector from "./GenerationModeSelector";
import TeseImportModal from "./TeseImportModal";
import TeseImportsHistory from "./TeseImportsHistory";
import FileDownload from "./FileDownload";
import "../pages/Contestacao.css"; // Importar estilos dos sliders
import {
  CONTESTATION_SERVICE,
  DOCUMENT_CREATION_PRODUCT_CODES,
  readServiceCustomization,
  checkServiceCustomizationExists,
  writeServiceCustomization,
  writeDossieCellRulesFromObservations,
  readDossieCellRulesAsObservations,
  readContestationCellRulesAsObservations,
  writeContestationCellRulesFromObservations,
  fileToBase64,
  downloadBase64File,
} from "../utils/serviceCustomizationApi";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid var(--ikaro-gray-medium)",
    padding: theme.spacing(2, 3),
  },
  titleIcon: {
    marginRight: theme.spacing(1),
    color: "var(--ikaro-orange-primary)",
  },
  titleText: {
    display: "flex",
    alignItems: "center",
    flex: 1,
  },
  titleCloseButton: {
    minWidth: "auto",
    padding: theme.spacing(1),
    color: "var(--ikaro-gray-dark)",
  },
  dialogContent: {
    padding: 0,
    minWidth: "800px",
    height: "70vh",
    display: "flex",
  },
  sidebar: {
    width: "200px",
    backgroundColor: "var(--ikaro-gray-light)",
    borderRight: "1px solid var(--ikaro-gray-medium)",
    display: "flex",
    flexDirection: "column",
  },
  sidebarItem: {
    padding: theme.spacing(2),
    cursor: "pointer",
    borderBottom: "1px solid var(--ikaro-gray-medium)",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "&.active": {
      backgroundColor: "var(--ikaro-orange-primary)",
      color: "white",
      fontWeight: "bold",
    },
  },
  contentArea: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
  },
  subtitle: {
    color: "var(--ikaro-gray-dark)",
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: "var(--ikaro-orange-primary)",
    display: "flex",
    alignItems: "center",
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  textField: {
    marginBottom: theme.spacing(2),
    "& .MuiInputBase-root": {
      backgroundColor: "var(--ikaro-bg-input)",
    },
  },
  textArea: {
    marginBottom: theme.spacing(2),
    "& .MuiInputBase-root": {
      backgroundColor: "var(--ikaro-bg-input)",
    },
    "& .MuiInputBase-input": {
      minHeight: "80px",
    },
  },
  fileUploadSection: {
    border: "2px dashed var(--ikaro-gray-medium)",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    textAlign: "center",
    backgroundColor: "var(--ikaro-gray-light)",
    marginBottom: theme.spacing(2),
    transition: "all 0.2s",
    "&:hover": {
      borderColor: "var(--ikaro-orange-primary)",
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "&.dragover": {
      borderColor: "var(--ikaro-orange-primary)",
      backgroundColor: "var(--ikaro-orange-light)",
    },
  },
  fileInput: {
    display: "none",
  },
  fileButton: {
    backgroundColor: "var(--ikaro-orange-primary)",
    color: "white",
    marginBottom: theme.spacing(1),
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-hover)",
    },
  },
  fileInfo: {
    fontSize: "14px",
    color: "var(--ikaro-gray-dark)",
  },
  currentFile: {
    backgroundColor: "var(--ikaro-bg-surface)",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    border: "1px solid var(--ikaro-border-default)",
    marginBottom: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: "1px solid var(--ikaro-gray-medium)",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  actionGroup: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  closeButton: {
    color: "var(--ikaro-gray-dark)",
    borderColor: "var(--ikaro-gray-medium)",
  },
  saveButton: {
    backgroundColor: "var(--ikaro-orange-primary) !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-hover) !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--ikaro-gray-medium) !important",
      color: "var(--ikaro-text-muted) !important",
    },
  },
  templateButton: {
    backgroundColor: "var(--ikaro-orange-primary) !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-hover) !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--ikaro-gray-medium) !important",
      color: "var(--ikaro-text-muted) !important",
    },
  },
  sectionSaveButton: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 3),
  },
  // Estilos para o Banco de Teses
  thesisBankHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  thesisTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  helpIcon: {
    color: "var(--ikaro-gray-dark)",
    cursor: "pointer",
    fontSize: "20px",
    "&:hover": {
      color: "var(--ikaro-orange-primary)",
    },
  },
  helpTooltip: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  },
  helpTooltipContent: {
    visibility: "hidden",
    width: "300px",
    backgroundColor: "var(--ikaro-bg-elevated)",
    color: "var(--ikaro-text-primary)",
    textAlign: "left",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    position: "absolute",
    zIndex: 1,
    top: "100%",
    left: "50%",
    marginLeft: "-150px",
    marginTop: theme.spacing(1),
    fontSize: "13px",
    lineHeight: "1.4",
    boxShadow: "var(--ikaro-shadow-medium)",
    border: "1px solid var(--ikaro-border-default)",
    "&::before": {
      content: '""',
      position: "absolute",
      bottom: "100%",
      left: "50%",
      marginLeft: "-5px",
      borderWidth: "5px",
      borderStyle: "solid",
      borderColor: "transparent transparent var(--ikaro-border-default) transparent",
    },
  },
  helpTooltipVisible: {
    visibility: "visible",
  },
  downloadButton: {
    backgroundColor: "var(--ikaro-orange-primary)",
    color: "white",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-hover)",
    },
    "&:disabled": {
      backgroundColor: "var(--ikaro-gray-medium)",
      color: "var(--ikaro-gray-dark)",
    },
  },
  uploadZipButton: {
    borderColor: "var(--ikaro-orange-primary)",
    color: "var(--ikaro-orange-primary)",
    "&:hover": {
      borderColor: "var(--ikaro-orange-hover)",
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "&:disabled": {
      borderColor: "var(--ikaro-gray-medium)",
      color: "var(--ikaro-gray-dark)",
    },
  },
  thesisJobProgress: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "var(--ikaro-gray-light)",
    borderRadius: theme.spacing(1),
  },
  thesisJobErrorsList: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    borderLeft: "3px solid var(--ikaro-orange-primary)",
  },
  thesisJobErrorItem: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  thesisList: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  thesisItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    borderRadius: 6,
    border: "1px solid var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-bg-surface)",
    overflow: "hidden",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: "var(--ikaro-orange-light)",
      boxShadow: "var(--ikaro-shadow-medium, 0 2px 6px rgba(0,0,0,0.06))",
    },
  },
  thesisItemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
  },
  thesisTemasPanel: {
    padding: theme.spacing(1, 2, 1.5, 5),
    borderTop: "1px solid var(--ikaro-border-light)",
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
  },
  thesisItemTexts: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  thesisTemasList: {
    margin: 0,
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(2.5),
    listStyle: "disc",
    listStylePosition: "outside",
    color: "var(--ikaro-text-primary)",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  thesisTemaItem: {
    marginBottom: theme.spacing(0.5),
    wordBreak: "break-word",
    display: "list-item",
  },
  thesisNoThemes: {
    fontSize: "12px",
    color: "var(--ikaro-text-muted)",
    fontStyle: "italic",
    marginTop: theme.spacing(0.25),
  },
  thesisDeleteButton: {
    color: "var(--ikaro-status-error-text)",
    flexShrink: 0,
    opacity: 0.65,
    transition: "opacity 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      opacity: 1,
      backgroundColor: "var(--ikaro-status-error-bg)",
    },
  },
  thesisProcessNumber: {
    display: "inline-flex",
    alignItems: "center",
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--ikaro-orange-primary)",
    backgroundColor: "var(--ikaro-orange-light)",
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    whiteSpace: "nowrap",
  },
  thesisFileName: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: "var(--ikaro-text-secondary)",
    fontSize: "0.85rem",
    minWidth: 0,
    flex: 1,
    "& > svg": {
      flexShrink: 0,
      fontSize: "1rem",
      color: "var(--ikaro-text-muted)",
    },
    "& > span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  thesisFileDownloadWrap: {
    // Captura o clique antes de chegar na linha (que abre/fecha temas).
    // O FileDownload renderiza o filename + botões próprios; deixamos ele
    // ocupar a largura disponível mantendo alinhamento com o NUP.
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    textAlign: "center",
    padding: theme.spacing(5, 2),
    color: "var(--ikaro-text-secondary)",
    border: "1px dashed var(--ikaro-border-default)",
    borderRadius: 8,
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
  },
  emptyStateIcon: {
    fontSize: "44px",
    color: "var(--ikaro-text-muted)",
    opacity: 0.6,
  },
  errorMessage: {
    backgroundColor: "var(--ikaro-status-error-bg)",
    color: "var(--ikaro-status-error-text)",
    border: "1px solid var(--ikaro-status-error)",
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  loadingState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    color: "var(--ikaro-text-secondary)",
  },
  thesisCount: {
    color: "var(--ikaro-text-secondary)",
    fontSize: "0.8rem",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    fontWeight: 600,
  },
  textAreaContainer: {
    position: "relative",
    marginBottom: theme.spacing(2),
  },
  expandButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
    backgroundColor: "var(--ikaro-white-semi)",
    padding: theme.spacing(0.5),
    minWidth: "auto",
    border: "1px solid var(--ikaro-border-default)",
    color: "var(--ikaro-text-primary)",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
    },
  },
  fullscreenDialog: {
    "& .MuiDialog-paper": {
      margin: 0,
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
    },
  },
  fullscreenContent: {
    padding: theme.spacing(3),
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  fullscreenTextArea: {
    flex: 1,
    "& .MuiInputBase-root": {
      height: "100%",
      alignItems: "flex-start",
    },
    "& .MuiInputBase-input": {
      height: "calc(100vh - 200px) !important",
      overflow: "auto !important",
      minHeight: "400px",
    },
  },
}));

/** Tenta corrigir nome que veio com encoding errado (ex: UTF-8 interpretado como Latin-1). Só aplica se parecer mojibake. */
function fixFileNameEncoding(str) {
  if (typeof str !== "string" || !str) return str;
  // Indícios de mojibake: Â, Ã, ╠, º sozinhos, etc. (UTF-8 bytes lidos como Latin-1)
  if (!/[\u00c0-\u00ff\u2560-\u2561]/.test(str)) return str;
  try {
    const bytes = new Uint8Array([...str].map((c) => c.charCodeAt(0) & 0xff));
    const decoded = new TextDecoder("utf-8").decode(bytes);
    return decoded;
  } catch {
    return str;
  }
}

const CellParamsModal = ({
  open,
  onClose,
  cellId,
  /** "contestacao" = modal completo; "dossie" = só Regras + Teses, regras salvas em DC_CONTESTACAO_DOSSIE */
  variant = "contestacao",
  showSaveButtons = true, // Controla se mostra os botões de salvar
  onSave = null, // Callback para quando salvar (modo externo)
  customizationParams: externalCustomizationParams = null, // Parâmetros externos
  observations: externalObservations = null, // Observações externas
  onParamsChange = null, // Callback para mudanças nos parâmetros
  onObservationsChange = null, // Callback para mudanças nas observações
}) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const isDossie = variant === "dossie";
  const { t: tCommon } = useTranslation("common");
  const { token, hasRole } = useAuth();
  const docProductCode = isDossie
    ? DOCUMENT_CREATION_PRODUCT_CODES.DOSSIE
    : DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO;
  const canUploadThesesZip =
    hasRole("ADMIN", "admin") || hasRole("ADMIN", "upload_teses");

  // Estados para os parâmetros de personalização (usa externos se fornecidos)
  const [customizationParams, setCustomizationParams] = useState(
    externalCustomizationParams || {
      creativity: 2,
      tone: 2,
      scope_topics: 2,
      complexity: 2,
      foundation_depth: 2,
    },
  );

  // Estados para as observações (usa externos se fornecidos)
  const [observations, setObservations] = useState(
    externalObservations || {
      client: "",
      internal: "",
      subsidios: "",
    },
  );

  // Estados para o template
  const [templateFile, setTemplateFile] = useState(null);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [templateUploading, setTemplateUploading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [saving, setSaving] = useState(false);
  const cancelRetryRef = useRef(false);
  const retryTimerRef = useRef(null);

  // Estado para controlar a aba selecionada
  const [selectedTab, setSelectedTab] = useState("generationMode");

  // Estados para modal fullscreen de textarea
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenField, setFullscreenField] = useState(null); // 'client', 'internal', 'subsidios'
  const [fullscreenValue, setFullscreenValue] = useState("");

  // Estado para revisão obrigatória (default: true)
  const [reviewRequired, setReviewRequired] = useState(true);

  // Estados para o Modo de Geração
  const [generationMode, setGenerationMode] = useState("standard");
  const [initialGenerationMode, setInitialGenerationMode] = useState("standard");
  const [savingGenerationMode, setSavingGenerationMode] = useState(false);

  // Estados para o Banco de Teses
  const [thesisList, setThesisList] = useState([]);
  const [thesisLoading, setThesisLoading] = useState(false);
  const [thesisDownloading, setThesisDownloading] = useState(false);
  const [thesisError, setThesisError] = useState(null);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [thesisLoaded, setThesisLoaded] = useState(false); // Controla se já tentou carregar
  // Importação individual (DC_TESE_IMPORT)
  const [teseImportModalOpen, setTeseImportModalOpen] = useState(false);
  const teseImportsHistoryRef = useRef(null);
  // Sub-aba dentro do painel "Banco de Teses": "list" (lista atual) ou
  // "import" (botões de upload + histórico de importações DC_TESE_IMPORT).
  const [thesisSubTab, setThesisSubTab] = useState("list");
  // Upload ZIP e progresso (consome_pasta)
  const [thesisUploading, setThesisUploading] = useState(false);
  const [thesisJobId, setThesisJobId] = useState(null);
  const [thesisJobStatus, setThesisJobStatus] = useState(null); // 'pending' | 'running' | 'completed' | 'failed'
  const [thesisJobResult, setThesisJobResult] = useState(null);
  const [thesisJobError, setThesisJobError] = useState(null);
  const [thesisDeletingId, setThesisDeletingId] = useState(null);
  const [thesisTitulosByDoc, setThesisTitulosByDoc] = useState({});
  const [thesisTitulosLoadingIds, setThesisTitulosLoadingIds] = useState(
    () => new Set(),
  );
  const [thesisTitulosErrorIds, setThesisTitulosErrorIds] = useState(
    () => new Set(),
  );
  const thesisTitulosByDocRef = useRef({});
  const thesisTitulosLoadingIdsRef = useRef(new Set());
  const thesisZipInputRef = useRef(null);
  const loadTitulosTemasForDocRef = useRef(null);

  useEffect(() => {
    thesisTitulosByDocRef.current = thesisTitulosByDoc;
  }, [thesisTitulosByDoc]);

  useEffect(() => {
    thesisTitulosLoadingIdsRef.current = thesisTitulosLoadingIds;
  }, [thesisTitulosLoadingIds]);

  // Função interna que busca todos os dados e aplica de uma vez antes de liberar a UI
  const fetchCellData = useCallback(async () => {
    if (!cellId || !token) return;

    if (isDossie) {
      const obs = await readDossieCellRulesAsObservations(token, cellId);
      setObservations({
        client: obs.client || "",
        internal: obs.internal || "",
        subsidios: obs.subsidios || "",
      });
      setHasTemplate(false);
      setReviewRequired(true);
      setGenerationMode("standard");
      setInitialGenerationMode("standard");
      setCustomizationParams({
        creativity: 2,
        tone: 2,
        scope_topics: 2,
        complexity: 2,
        foundation_depth: 2,
      });
      return;
    }

    const defaultParams = {
      creativity: 2,
      tone: 2,
      scope_topics: 2,
      complexity: 2,
      foundation_depth: 2,
    };

    // Buscar tudo em paralelo (regras: blob DC_CONTESTACAO + fallback chaves legadas)
    const [
      profileParams,
      rulesObs,
      templateExists,
      reviewRequiredValue,
      generationModeValue,
    ] = await Promise.all([
      readServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.PROFILE_PARAMS,
        DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
      ),
      readContestationCellRulesAsObservations(token, cellId),
      checkServiceCustomizationExists(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.DOCX_TEMPLATE_FILE,
        DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
      ),
      readServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.REVIEW_REQUIRED,
        DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
      ),
      readServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.GENERATION_MODE,
        DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
      ),
    ]);

    // Aplicar tudo de uma vez
    const finalParams =
      profileParams !== null && profileParams !== undefined
        ? { ...defaultParams, ...profileParams }
        : defaultParams;

    const modeValue =
      generationModeValue && generationModeValue !== ""
        ? generationModeValue
        : "standard";

    setCustomizationParams(finalParams);
    setObservations({
      client: rulesObs.client || "",
      internal: rulesObs.internal || "",
      subsidios: rulesObs.subsidios || "",
    });
    setHasTemplate(templateExists);
    setReviewRequired(
      reviewRequiredValue !== null && reviewRequiredValue !== undefined
        ? reviewRequiredValue
        : true
    );
    setGenerationMode(modeValue);
    setInitialGenerationMode(modeValue);
  }, [cellId, token, isDossie]);

  // Função para carregar dados da célula com retry automático
  const loadCellData = useCallback(async () => {
    if (!cellId || !token) return;

    cancelRetryRef.current = false;
    setLoading(true);

    const attemptLoad = async () => {
      try {
        await fetchCellData();
        setLoadError(false);
        setLoading(false);
      } catch (error) {
        console.error("Erro ao carregar dados da célula:", error);

        if (cancelRetryRef.current) {
          setLoading(false);
          return;
        }

        setLoadError(true);

        // Countdown de 3 segundos antes de tentar novamente
        for (let s = 3; s > 0; s--) {
          if (cancelRetryRef.current) {
            setLoading(false);
            return;
          }
          setRetryCountdown(s);
          await new Promise((resolve) => {
            retryTimerRef.current = setTimeout(resolve, 1000);
          });
        }

        if (!cancelRetryRef.current) {
          setRetryCountdown(0);
          setLoadError(false);
          attemptLoad();
        } else {
          setLoading(false);
        }
      }
    };

    attemptLoad();
  }, [cellId, token, fetchCellData]);

  const handleCancelLoad = useCallback(() => {
    cancelRetryRef.current = true;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Reset inicial e carregar dados da célula
  useEffect(() => {
    if (open) {
      // Se não está usando valores externos, resetar para padrões antes de carregar
      if (!externalCustomizationParams) {
        setCustomizationParams({
          creativity: 2,
          tone: 2,
          scope_topics: 2,
          complexity: 2,
          foundation_depth: 2,
        });
      }

      if (!externalObservations) {
        setObservations({
          client: "",
          internal: "",
          subsidios: "",
        });
      }

      // Resetar revisão obrigatória para padrão
      setReviewRequired(true);

      // Resetar modo de geração para padrão
      setGenerationMode("standard");
      setInitialGenerationMode("standard");

      // Carregar dados da API apenas se tiver cellId e não estiver usando valores externos
      if (cellId && !externalCustomizationParams && !externalObservations) {
        loadCellData();
      }
    }
  }, [
    open,
    cellId,
    externalCustomizationParams,
    externalObservations,
    loadCellData,
  ]);

  useEffect(() => {
    if (open && isDossie) {
      setSelectedTab("customRules");
    }
  }, [open, isDossie]);

  // Sincronizar com valores externos quando mudarem
  useEffect(() => {
    if (externalCustomizationParams) {
      setCustomizationParams(externalCustomizationParams);
    }
  }, [externalCustomizationParams]);

  useEffect(() => {
    if (externalObservations) {
      setObservations(externalObservations);
    }
  }, [externalObservations]);

  const hasInternalPermission = () => {
    return hasRole(
      "DOCUMENT_CREATION",
      "edit_custom_super_user",
      docProductCode,
    );
  };

  const saveRulesToServer = async (obsToSave) => {
    if (isDossie) {
      return writeDossieCellRulesFromObservations(
        token,
        cellId,
        {
          client: obsToSave.client,
          internal: obsToSave.internal,
          subsidios: obsToSave.subsidios,
        },
        hasInternalPermission(),
      );
    }
    const contestOk = await writeContestationCellRulesFromObservations(
      token,
      cellId,
      {
        client: obsToSave.client,
        internal: obsToSave.internal,
        subsidios: obsToSave.subsidios,
      },
      hasInternalPermission(),
    );
    const reviewSuccess = await writeServiceCustomization(
      token,
      cellId,
      CONTESTATION_SERVICE.ATTRIBUTES.REVIEW_REQUIRED,
      reviewRequired,
      DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
    );
    return contestOk && reviewSuccess;
  };

  const handleSaveParams = async () => {
    setSaving(true);
    try {
      const success = await writeServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.PROFILE_PARAMS,
        customizationParams,
        docProductCode,
      );

      if (success) {
        console.log("Parâmetros salvos com sucesso");
      } else {
        console.error("Erro ao salvar parâmetros");
      }
    } catch (error) {
      console.error("Erro ao salvar parâmetros:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomRules = async () => {
    setSaving(true);
    try {
      const ok = await saveRulesToServer(observations);
      if (ok) {
        console.log("Regras customizadas salvas com sucesso");
      } else {
        console.error("Erro ao salvar regras customizadas");
      }
    } catch (error) {
      console.error("Erro ao salvar regras customizadas:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGenerationMode = async () => {
    setSavingGenerationMode(true);
    try {
      const success = await writeServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.GENERATION_MODE,
        generationMode,
        docProductCode,
      );

      if (success) {
        setInitialGenerationMode(generationMode);
        console.log("Modo de geração salvo com sucesso");
      } else {
        console.error("Erro ao salvar modo de geração");
      }
    } catch (error) {
      console.error("Erro ao salvar modo de geração:", error);
    } finally {
      setSavingGenerationMode(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateFile) return;

    setTemplateUploading(true);
    try {
      // Converter arquivo para base64
      const base64 = await fileToBase64(templateFile);

      // Salvar o arquivo como base64
      const success = await writeServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.DOCX_TEMPLATE_FILE,
        base64,
        docProductCode,
      );

      if (success) {
        setHasTemplate(true);
        setTemplateFile(null);
        console.log("Template salvo com sucesso");
      } else {
        console.error("Erro ao salvar template");
      }
    } catch (error) {
      console.error("Erro ao salvar template:", error);
    } finally {
      setTemplateUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const templateBase64 = await readServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.DOCX_TEMPLATE_FILE,
        docProductCode,
      );

      if (templateBase64) {
        downloadBase64File(templateBase64, "template.docx");
      } else {
        console.error("Template não encontrado");
      }
    } catch (error) {
      console.error("Erro ao baixar template:", error);
    }
  };

  const handleObservationChange = (field, value) => {
    const newObservations = {
      ...observations,
      [field]: value,
    };
    setObservations(newObservations);

    // Chamar callback externo se fornecido
    if (onObservationsChange) {
      onObservationsChange(newObservations);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      setTemplateFile(file);
    } else {
      alert("Por favor, selecione apenas arquivos DOCX");
    }
  };

  // Função para carregar lista de teses
  const loadThesisList = useCallback(async () => {
    if (!cellId || !token) return;

    setThesisLoading(true);
    setThesisError(null);

    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/files/documents/list`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          cell_id: cellId,
          tipo_documento: "TESE",
          service_code: "DOCUMENT_CREATION",
          product_code: docProductCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Erro ao carregar lista de teses");
      }

      const data = await response.json();
      setThesisList(data.documentos || []);
      setThesisLoaded(true);
    } catch (error) {
      console.error("Erro ao carregar lista de teses:", error);
      setThesisError(error.message);
      setThesisLoaded(true); // Marca como tentou carregar mesmo com erro
    } finally {
      setThesisLoading(false);
    }
  }, [cellId, token, docProductCode]);

  const loadTitulosTemasForDoc = useCallback(
    async (thesis) => {
      const idDoc = thesis?.id_documento;
      if (idDoc == null || !cellId || !token) return;
      if (Array.isArray(thesis.titulos_temas)) {
        setThesisTitulosByDoc((prev) =>
          Array.isArray(prev[idDoc]) ? prev : { ...prev, [idDoc]: thesis.titulos_temas },
        );
        return;
      }
      if (Array.isArray(thesisTitulosByDocRef.current[idDoc])) return;
      if (thesisTitulosLoadingIdsRef.current.has(idDoc)) return;

      setThesisTitulosLoadingIds((prev) => new Set(prev).add(idDoc));
      setThesisTitulosErrorIds((prev) => {
        const next = new Set(prev);
        next.delete(idDoc);
        return next;
      });
      try {
        const API_BASE_URL = getApiBaseUrl();
        const response = await fetch(
          `${API_BASE_URL}/files/documents/tese-titulos-temas`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              cell_id: cellId,
              id_documento: idDoc,
              service_code: "DOCUMENT_CREATION",
              product_code: docProductCode,
            }),
          },
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(
            errData.detail || t("contestacao.cellParams.thesisBank.temasLoadError"),
          );
        }
        const data = await response.json();
        setThesisTitulosByDoc((prev) => ({
          ...prev,
          [idDoc]: data.titulos || [],
        }));
      } catch (err) {
        console.error("Erro ao carregar títulos dos temas:", err);
        setThesisTitulosErrorIds((prev) => new Set(prev).add(idDoc));
      } finally {
        setThesisTitulosLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(idDoc);
          return next;
        });
      }
    },
    [cellId, token, docProductCode, t],
  );

  useEffect(() => {
    loadTitulosTemasForDocRef.current = loadTitulosTemasForDoc;
  }, [loadTitulosTemasForDoc]);

  // Ao receber uma nova lista de teses, dispara o carregamento dos temas.
  // Não depende de `loadTitulosTemasForDoc` (que muda a cada render por causa
  // do `t`) para evitar loop infinito; usa ref para acessar a versão atual.
  useEffect(() => {
    if (!thesisList.length) return;
    const loader = loadTitulosTemasForDocRef.current;
    if (!loader) return;
    thesisList.forEach((thesis) => {
      void loader(thesis);
    });
  }, [thesisList]);

  // Função para baixar todas as teses em ZIP
  const handleDownloadTheses = async () => {
    if (!cellId || !token) return;

    setThesisDownloading(true);
    setThesisError(null);

    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(
        `${API_BASE_URL}/files/documents/download-zip`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cell_id: cellId,
            tipo_documento: "TESE",
            service_code: "DOCUMENT_CREATION",
            product_code: docProductCode,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            t("contestacao.cellParams.thesisBank.downloadError"),
        );
      }

      // Converter response em blob e fazer download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `documentos_celula_${cellId}_tese.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error("Erro ao baixar teses:", error);
      setThesisError(error.message);
    } finally {
      setThesisDownloading(false);
    }
  };

  // Carregar lista de teses quando a aba for selecionada (apenas uma vez)
  useEffect(() => {
    if (
      open &&
      selectedTab === "thesisBank" &&
      cellId &&
      !thesisLoaded &&
      !thesisLoading
    ) {
      loadThesisList();
    }
  }, [open, selectedTab, cellId, thesisLoaded, thesisLoading, loadThesisList]);

  // Reset do estado de teses e cancelar loading quando o modal fecha
  useEffect(() => {
    if (!open) {
      setThesisList([]);
      setThesisLoaded(false);
      setThesisError(null);
      setThesisDeletingId(null);
      setThesisTitulosByDoc({});
      setThesisTitulosLoadingIds(new Set());
      setThesisTitulosErrorIds(new Set());
      handleCancelLoad();
    }
  }, [open, handleCancelLoad]);

  const handleDeleteThesis = useCallback(
    async (thesis) => {
      const idDoc = thesis?.id_documento;
      if (!cellId || !token || idDoc == null) return;
      const nome =
        fixFileNameEncoding(thesis.nome_arquivo || "") || thesis.nome_arquivo || "";
      const ok = window.confirm(
        t("contestacao.cellParams.thesisBank.deleteConfirm", { nome }),
      );
      if (!ok) return;

      setThesisDeletingId(idDoc);
      setThesisError(null);
      try {
        const API_BASE_URL = getApiBaseUrl();
        const response = await fetch(
          `${API_BASE_URL}/contestacao_teses/documento/excluir`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              cell_id: cellId,
              id_documento: idDoc,
            }),
          },
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(
            errData.detail ||
              t("contestacao.cellParams.thesisBank.deleteError"),
          );
        }
        await loadThesisList();
      } catch (err) {
        console.error("Erro ao excluir tese:", err);
        setThesisError(err.message || t("contestacao.cellParams.thesisBank.deleteError"));
      } finally {
        setThesisDeletingId(null);
      }
    },
    [cellId, token, t, loadThesisList],
  );

  // Função para tentar novamente carregar as teses (acionada pelo usuário)
  const handleRetryLoadThesis = () => {
    setThesisLoaded(false); // Reset para permitir nova tentativa
    setThesisError(null);
    loadThesisList();
  };

  // Upload ZIP: abre seletor de arquivo
  const handleUploadZipClick = () => {
    thesisZipInputRef.current?.click();
  };

  // Upload ZIP: envia arquivo e inicia acompanhamento
  const handleZipFileChange = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      if (!file || !cellId || !token) return;
      if (!file.name.toLowerCase().endsWith(".zip")) {
        setThesisJobError(t("contestacao.cellParams.thesisBank.uploadZipOnly"));
        return;
      }
      e.target.value = "";
      setThesisUploading(true);
      setThesisJobId(null);
      setThesisJobStatus(null);
      setThesisJobResult(null);
      setThesisJobError(null);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const formData = new FormData();
        formData.append("arquivo_zip", file);
        formData.append("cell_id", cellId);
        const response = await fetch(
          `${API_BASE_URL}/contestacao_teses/consome_pasta`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          },
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || response.statusText);
        }
        const data = await response.json();
        setThesisJobId(data.job_id);
        setThesisJobStatus("pending");
      } catch (err) {
        setThesisJobError(
          err.message || t("contestacao.cellParams.thesisBank.uploadError"),
        );
        setThesisJobStatus("failed");
      } finally {
        setThesisUploading(false);
      }
    },
    [cellId, token, t],
  );

  // Polling do status do job de processamento de ZIP
  useEffect(() => {
    if (
      !thesisJobId ||
      !token ||
      thesisJobStatus === "completed" ||
      thesisJobStatus === "failed"
    )
      return;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const interval = setInterval(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/contestacao_teses/consome_pasta/status/${thesisJobId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) return;
        const data = await response.json();
        setThesisJobStatus(data.status);
        if (data.status === "completed" && data.result) {
          setThesisJobResult(data.result);
          setThesisJobError(null);
          loadThesisList(); // Atualiza lista de teses
        }
        if (data.status === "failed" && data.error) {
          setThesisJobError(
            data.error.mensagem || data.error.detail || "Erro no processamento",
          );
        }
      } catch (err) {
        setThesisJobError(err.message);
        setThesisJobStatus("failed");
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [thesisJobId, thesisJobStatus, token, loadThesisList]);

  // Funções para expandir textarea em tela cheia
  const handleExpandTextarea = (field) => {
    setFullscreenField(field);
    setFullscreenValue(observations[field] || "");
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    // Salvar o valor quando fechar
    if (fullscreenField) {
      handleObservationChange(fullscreenField, fullscreenValue);
    }
    setFullscreenOpen(false);
    setFullscreenField(null);
    setFullscreenValue("");
  };

  const handleSaveAndCloseFullscreen = async () => {
    // Atualizar o estado local primeiro
    if (fullscreenField) {
      handleObservationChange(fullscreenField, fullscreenValue);
    }

    // Se showSaveButtons for true e tiver cellId, salvar no servidor
    if (showSaveButtons && cellId) {
      setSaving(true);
      try {
        const updatedObservations = {
          ...observations,
          [fullscreenField]: fullscreenValue,
        };
        const ok = await saveRulesToServer(updatedObservations);
        if (ok) {
          console.log("Regras customizadas salvas com sucesso");
        } else {
          console.error("Erro ao salvar regras customizadas");
        }
      } catch (error) {
        console.error("Erro ao salvar regras:", error);
      } finally {
        setSaving(false);
      }
    }

    // Fechar o modal
    setFullscreenOpen(false);
    setFullscreenField(null);
    setFullscreenValue("");
  };

  // Renderizar conteúdo baseado na aba selecionada
  const renderContent = () => {
    if (selectedTab === "parameters") {
      return (
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            ⚙️ {t("contestacao.cellParams.sections.customization")}
          </Typography>

          <CustomizationSliders
            initialValues={customizationParams}
            onChange={(newParams) => {
              setCustomizationParams(newParams);
              // Chamar callback externo se fornecido
              if (onParamsChange) {
                onParamsChange(newParams);
              }
            }}
          />

          {/* Botão de salvar apenas parâmetros (apenas se showSaveButtons for true) */}
          {showSaveButtons && (
            <Button
              onClick={handleSaveParams}
              variant="contained"
              className={`${classes.saveButton} ${classes.sectionSaveButton}`}
              disabled={saving || loading}
              fullWidth
            >
              {saving
                ? "Salvando..."
                : t("contestacao.cellParams.sections.saveCustomization")}
            </Button>
          )}
        </Box>
      );
    }

    if (selectedTab === "customRules") {
      return (
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            📋{" "}
            {isDossie
              ? t("contestacaoDossie.cellParams.sectionRules")
              : t("contestacao.cellParams.sections.customRules")}
          </Typography>

          {/* Regras Customizadas do Cliente */}
          <Box className={classes.textAreaContainer}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={
                isDossie
                  ? t("contestacaoDossieCreate.customRules.client")
                  : t("contestacao.cellParams.customRules.client")
              }
              placeholder={
                isDossie
                  ? t("contestacaoDossieCreate.customRules.clientPlaceholder")
                  : t("contestacao.cellParams.customRules.clientPlaceholder")
              }
              value={observations.client}
              onChange={(e) =>
                handleObservationChange("client", e.target.value)
              }
              className={classes.textArea}
              variant="outlined"
            />
            <Button
              className={classes.expandButton}
              onClick={() => handleExpandTextarea("client")}
              size="small"
              title="Expandir em tela cheia"
            >
              <Fullscreen fontSize="small" />
            </Button>
          </Box>

          {/* Regras para Subsídios */}
          {/* TODO: Remover hidden quando campo de regras de subsídio for reativado */}
          <Box className={classes.textAreaContainer}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={
                isDossie
                  ? t("contestacaoDossieCreate.parameters.subsidiosRules")
                  : "Regras para Subsídios"
              }
              placeholder={
                isDossie
                  ? t(
                      "contestacaoDossieCreate.parameters.subsidiosRulesPlaceholder",
                    )
                  : "Regras específicas para análise de subsídios na criação da contestação..."
              }
              value={observations.subsidios}
              onChange={(e) =>
                handleObservationChange("subsidios", e.target.value)
              }
              className={classes.textArea}
              variant="outlined"
              // style={{ display: "none" }}
            />
            <Button
              className={classes.expandButton}
              onClick={() => handleExpandTextarea("subsidios")}
              size="small"
              title="Expandir em tela cheia"
            >
              <Fullscreen fontSize="small" />
            </Button>
          </Box>

          {/* Regras Internas (apenas para CONTEST_PARAMS_SUPERUSER) */}
          {hasInternalPermission() && (
            <Box className={classes.textAreaContainer}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  isDossie
                    ? t("contestacaoDossieCreate.customRules.internal")
                    : t("contestacao.cellParams.customRules.internal")
                }
                placeholder={
                  isDossie
                    ? t("contestacaoDossieCreate.customRules.internalPlaceholder")
                    : t(
                        "contestacao.cellParams.customRules.internalPlaceholder",
                      )
                }
                value={observations.internal}
                onChange={(e) =>
                  handleObservationChange("internal", e.target.value)
                }
                className={classes.textArea}
                variant="outlined"
              />
              <Button
                className={classes.expandButton}
                onClick={() => handleExpandTextarea("internal")}
                size="small"
                title="Expandir em tela cheia"
              >
                <Fullscreen fontSize="small" />
              </Button>
            </Box>
          )}

          {/* Campo de revisão obrigatória (apenas contestação) */}
          {!isDossie && (
            <Box style={{ marginTop: "24px", marginBottom: "16px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reviewRequired}
                    onChange={(e) => setReviewRequired(e.target.checked)}
                    color="primary"
                  />
                }
                label="Revisão obrigatória"
              />
              <Typography
                variant="body2"
                style={{
                  marginTop: "8px",
                  color: "var(--ikaro-gray-dark)",
                  fontSize: "13px",
                }}
              >
                Quando marcado, todos os itens devem ser revisados antes de
                finalizar o documento. Quando desmarcado, é possível finalizar
                mesmo com itens não revisados.
              </Typography>
            </Box>
          )}

          {/* Botão de salvar regras customizadas (apenas se showSaveButtons for true) */}
          {showSaveButtons && (
            <Button
              onClick={handleSaveCustomRules}
              variant="contained"
              className={`${classes.saveButton} ${classes.sectionSaveButton}`}
              disabled={saving || loading}
              fullWidth
            >
              {saving
                ? "Salvando..."
                : t("contestacao.cellParams.customRules.saveRules")}
            </Button>
          )}
        </Box>
      );
    }

    if (selectedTab === "template") {
      return (
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            📄 {t("contestacao.cellParams.sections.template")}
          </Typography>

          <Typography
            variant="body2"
            className="mb-16"
            style={{ color: "var(--ikaro-gray-dark)" }}
          >
            {t("contestacao.cellParams.template.description")}
          </Typography>

          {/* Template atual */}
          {hasTemplate && (
            <Box className={classes.currentFile}>
              <Typography variant="body2">
                <strong>Template atual:</strong> template.docx
              </Typography>
              <Button
                onClick={handleDownloadTemplate}
                variant="outlined"
                size="small"
                startIcon={<GetApp />}
                style={{ marginTop: "8px" }}
              >
                Baixar Template Atual
              </Button>
            </Box>
          )}

          {/* Upload de novo template */}
          <Box className={classes.fileUploadSection}>
            <input
              accept=".docx"
              className={classes.fileInput}
              id="template-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="template-file-input">
              <Button
                variant="contained"
                component="span"
                className={classes.fileButton}
                startIcon={<CloudUpload />}
              >
                {t("contestacao.cellParams.template.selectFile")}
              </Button>
            </label>

            {templateFile ? (
              <Typography variant="body2" className={classes.fileInfo}>
                Arquivo selecionado: {templateFile.name}
              </Typography>
            ) : (
              <Typography variant="body2" className={classes.fileInfo}>
                {t("contestacao.cellParams.template.noFile")}
              </Typography>
            )}
          </Box>

          {/* Botão de salvar template (apenas se showSaveButtons for true) */}
          {showSaveButtons && (
            <Button
              onClick={handleSaveTemplate}
              variant="contained"
              className={`${classes.templateButton} ${classes.sectionSaveButton}`}
              disabled={templateUploading || !templateFile}
              startIcon={<CloudUpload />}
              fullWidth
            >
              {templateUploading
                ? t("contestacao.cellParams.template.uploading")
                : t("contestacao.cellParams.template.saveTemplate")}
            </Button>
          )}
        </Box>
      );
    }

    if (selectedTab === "generationMode") {
      const generationModeChanged = generationMode !== initialGenerationMode;

      return (
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            🚀 {t("contestacao.cellParams.generationMode.title")}
          </Typography>

          <GenerationModeSelector
            value={generationMode}
            onChange={(mode) => setGenerationMode(mode)}
          />

          {showSaveButtons && generationModeChanged && (
            <Button
              onClick={handleSaveGenerationMode}
              variant="contained"
              className={`${classes.saveButton} ${classes.sectionSaveButton}`}
              disabled={savingGenerationMode || loading}
              fullWidth
            >
              {savingGenerationMode
                ? "Salvando..."
                : t("contestacao.cellParams.generationMode.save")}
            </Button>
          )}
        </Box>
      );
    }

    if (selectedTab === "thesisBank") {
      return (
        <Box>
          {/* Header com título e tooltip de ajuda. Os botões de ação (download,
              upload ZIP, importar tese) ficam dentro da sub-aba correspondente
              para evitar misturar "consultar lista" com "importar/gerenciar". */}
          <Box className={classes.thesisBankHeader}>
            <Box className={classes.thesisTitleContainer}>
              <Typography
                variant="h6"
                className={classes.sectionTitle}
                style={{ marginBottom: 0 }}
              >
                📚 {t("contestacao.cellParams.sections.thesisBank")}
              </Typography>
              <Box
                className={classes.helpTooltip}
                onMouseEnter={() => setShowHelpTooltip(true)}
                onMouseLeave={() => setShowHelpTooltip(false)}
              >
                <HelpOutline className={classes.helpIcon} />
                <Box
                  className={`${classes.helpTooltipContent} ${
                    showHelpTooltip ? classes.helpTooltipVisible : ""
                  }`}
                >
                  {t(
                    isDossie
                      ? "contestacaoDossie.cellParams.thesisHelpTooltip"
                      : "contestacao.cellParams.thesisBank.helpTooltip",
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Sub-abas: Lista | Importação. A aba "Importação" só aparece para
              quem tem permissão (mesma regra do botão de upload anterior). */}
          <Tabs
            value={thesisSubTab}
            onChange={(_e, v) => setThesisSubTab(v)}
            indicatorColor="primary"
            textColor="primary"
            style={{ borderBottom: "1px solid var(--ikaro-border-default)", marginBottom: 12 }}
          >
            <Tab
              value="list"
              label={t("contestacao.cellParams.thesisBankTabs.list")}
            />
            {canUploadThesesZip && (
              <Tab
                value="import"
                label={t("contestacao.cellParams.thesisBankTabs.import")}
              />
            )}
          </Tabs>

          {thesisSubTab === "import" && canUploadThesesZip && (
            <Box>
              {/* Ações de importação: tese individual (DC_TESE_IMPORT) +
                  upload em lote via ZIP (legado super-admin/admin). */}
              <Box
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  className={classes.downloadButton}
                  startIcon={<LibraryBooks />}
                  onClick={() => setTeseImportModalOpen(true)}
                >
                  {t("contestacao.cellParams.thesisImport.importButton")}
                </Button>
                <input
                  type="file"
                  accept=".zip"
                  ref={thesisZipInputRef}
                  onChange={handleZipFileChange}
                  style={{ display: "none" }}
                />
                <Button
                  variant="outlined"
                  className={classes.uploadZipButton}
                  startIcon={<CloudUpload />}
                  onClick={handleUploadZipClick}
                  disabled={thesisUploading}
                >
                  {thesisUploading
                    ? t("contestacao.cellParams.thesisBank.uploading")
                    : t("contestacao.cellParams.thesisBank.uploadZip")}
                </Button>
              </Box>

              {/* Progresso do processamento do ZIP ou mensagem de erro */}
              {(thesisJobId || thesisJobError) && (
                <Box className={classes.thesisJobProgress}>
                  {thesisJobStatus === "pending" ||
                  thesisJobStatus === "running" ? (
                    <Typography variant="body2">
                      {t("contestacao.cellParams.thesisBank.progressProcessing")}
                    </Typography>
                  ) : thesisJobStatus === "completed" && thesisJobResult ? (
                    <Box style={{ width: "100%" }}>
                      <Typography variant="body2">
                        {t("contestacao.cellParams.thesisBank.progressDone", {
                          total: thesisJobResult.total,
                          success: thesisJobResult.sucesso,
                          errors: thesisJobResult.erros?.length ?? 0,
                        })}
                      </Typography>
                      {thesisJobResult.erros?.length > 0 && (
                        <Box className={classes.thesisJobErrorsList}>
                          <Typography variant="caption" display="block" style={{ fontWeight: 600, marginTop: 8 }}>
                            {t("contestacao.cellParams.thesisBank.progressErrorsTitle")}
                          </Typography>
                          {thesisJobResult.erros.map((erro, idx) => (
                            <Box key={idx} className={classes.thesisJobErrorItem}>
                              <Typography variant="caption" component="span" style={{ fontWeight: 500 }}>
                                {fixFileNameEncoding((typeof erro.arquivo === "string" ? erro.arquivo.replace(/^.*[/\\]/, "") : null) || "") || t("contestacao.cellParams.thesisBank.unknownFile")}
                              </Typography>
                              <Typography variant="caption" display="block" color="error">
                                {erro.mensagem || ""}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : thesisJobError ? (
                    <Typography variant="body2" color="error">
                      {thesisJobError}
                    </Typography>
                  ) : null}
                  {(thesisJobStatus === "completed" ||
                    thesisJobStatus === "failed" ||
                    (thesisJobError && !thesisJobId)) && (
                    <Button
                      size="small"
                      onClick={() => {
                        setThesisJobId(null);
                        setThesisJobStatus(null);
                        setThesisJobResult(null);
                        setThesisJobError(null);
                      }}
                      style={{ marginLeft: 8 }}
                    >
                      {t("contestacao.cellParams.thesisBank.progressDismiss")}
                    </Button>
                  )}
                </Box>
              )}

              {/* Histórico de importações individuais (DC_TESE_IMPORT). */}
              <Box style={{ marginTop: 16 }}>
                <TeseImportsHistory
                  ref={teseImportsHistoryRef}
                  cellId={cellId}
                />
              </Box>
            </Box>
          )}

          {thesisSubTab === "list" && (
            <Box>
              {/* Toolbar da lista: refresh (sempre visível, útil para reler
                  o banco vetorial depois de criar/excluir teses) + download
                  (só com lista carregada). */}
              <Box
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <Tooltip
                  title={t("contestacao.cellParams.thesisBank.refresh")}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleRetryLoadThesis}
                      disabled={thesisLoading}
                      aria-label={t("contestacao.cellParams.thesisBank.refresh")}
                    >
                      {thesisLoading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Refresh fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {thesisList.length > 0 && (
                  <Button
                    variant="contained"
                    className={classes.downloadButton}
                    startIcon={<GetApp />}
                    onClick={handleDownloadTheses}
                    disabled={thesisDownloading}
                  >
                    {thesisDownloading
                      ? t("contestacao.cellParams.thesisBank.downloading")
                      : t("contestacao.cellParams.thesisBank.downloadAll")}
                  </Button>
                )}
              </Box>

              {/* Mensagem de erro com botão de retry */}
              {thesisError && (
                <Box className={classes.errorMessage}>
                  <Typography variant="body2" style={{ flex: 1 }}>
                    {thesisError}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRetryLoadThesis}
                    style={{
                      marginLeft: "16px",
                      borderColor: "#c62828",
                      color: "#c62828",
                    }}
                  >
                    {t("contestacao.cellParams.thesisBank.retry")}
                  </Button>
                </Box>
              )}

              {/* Estado de carregamento */}
              {thesisLoading && (
                <Box className={classes.loadingState}>
                  <Typography variant="body1">
                    {t("contestacao.cellParams.thesisBank.loading")}
                  </Typography>
                </Box>
              )}

              {/* Lista vazia */}
              {!thesisLoading && thesisList.length === 0 && !thesisError && (
                <Box className={classes.emptyState}>
                  <LibraryBooks className={classes.emptyStateIcon} />
                  <Typography variant="body1">
                    {t("contestacao.cellParams.thesisBank.emptyState")}
                  </Typography>
                </Box>
              )}

              {/* Lista de teses */}
              {!thesisLoading && thesisList.length > 0 && (
            <>
              <Typography variant="body2" className={classes.thesisCount}>
                {t("contestacao.cellParams.thesisBank.totalDocuments", {
                  count: thesisList.length,
                })}
              </Typography>
              <Box className={classes.thesisList}>
                {thesisList.map((thesis) => {
                  const idDoc = thesis.id_documento;
                  const titulosLazy = thesisTitulosByDoc[idDoc];
                  const titulos = Array.isArray(titulosLazy)
                    ? titulosLazy
                    : Array.isArray(thesis.titulos_temas)
                      ? thesis.titulos_temas
                      : undefined;
                  const loadingTemas = thesisTitulosLoadingIds.has(idDoc);
                  const temasErro = thesisTitulosErrorIds.has(idDoc);
                  return (
                    <Box key={idDoc} className={classes.thesisItem}>
                      <Box className={classes.thesisItemRow}>
                        <Box className={classes.thesisItemTexts}>
                          <Typography
                            component="span"
                            className={classes.thesisProcessNumber}
                          >
                            {thesis.numero_processo ||
                              t("contestacao.cellParams.thesisBank.noProcessNumber")}
                          </Typography>
                          {thesis.token ? (
                            // Botão de download + preview do PDF. `stopPropagation`
                            // no wrapper evita que o clique no botão (ou no menu
                            // de preview) propague para a linha e abra/feche os
                            // temas, que é o comportamento padrão da linha.
                            <Box
                              className={classes.thesisFileDownloadWrap}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <FileDownload
                                file={{
                                  filename:
                                    thesis.filename || thesis.nome_arquivo,
                                  token: thesis.token,
                                }}
                                previewType="pdf"
                              />
                            </Box>
                          ) : (
                            <Typography
                              component="span"
                              className={classes.thesisFileName}
                            >
                              <Description />
                              <span title={thesis.nome_arquivo || ""}>
                                {fixFileNameEncoding(thesis.nome_arquivo || "") ||
                                  thesis.nome_arquivo}
                              </span>
                            </Typography>
                          )}
                        </Box>
                        {canUploadThesesZip && (
                          <IconButton
                            size="small"
                            className={classes.thesisDeleteButton}
                            onClick={() => handleDeleteThesis(thesis)}
                            disabled={thesisDeletingId != null}
                            title={t("contestacao.cellParams.thesisBank.deleteThesis")}
                            aria-label={t("contestacao.cellParams.thesisBank.deleteThesis")}
                          >
                            {thesisDeletingId === idDoc ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <DeleteOutline fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </Box>
                      <Box className={classes.thesisTemasPanel}>
                        {loadingTemas && (
                          <Box display="flex" alignItems="center" py={1}>
                            <CircularProgress size={22} />
                          </Box>
                        )}
                        {!loadingTemas && temasErro && (
                          <Typography variant="body2" color="error">
                            {t("contestacao.cellParams.thesisBank.temasLoadError")}
                          </Typography>
                        )}
                        {!loadingTemas &&
                          !temasErro &&
                          Array.isArray(titulos) &&
                          titulos.length === 0 && (
                            <Typography variant="body2" color="textSecondary">
                              {t("contestacao.cellParams.thesisBank.temasEmpty")}
                            </Typography>
                          )}
                        {!loadingTemas &&
                          !temasErro &&
                          Array.isArray(titulos) &&
                          titulos.length > 0 && (
                            <Box
                              component="ul"
                              className={classes.thesisTemasList}
                            >
                              {titulos.map((titulo, ti) => (
                                <Box
                                  component="li"
                                  key={ti}
                                  className={classes.thesisTemaItem}
                                >
                                  {titulo}
                                </Box>
                              ))}
                            </Box>
                          )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
            </Box>
          )}
        </Box>
      );
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleText}>
          <Settings className={classes.titleIcon} />
          <Typography variant="h6">
            {isDossie
              ? t("contestacaoDossie.cellParams.title")
              : t("contestacao.cellParams.title")}
          </Typography>
        </Box>
        <Button
          className={classes.titleCloseButton}
          onClick={onClose}
          size="small"
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Menu lateral */}
        <Box className={classes.sidebar}>
          {isDossie ? (
            <>
              <Box
                className={`${classes.sidebarItem} cell-params-sidebar-highlight ${
                  selectedTab === "customRules" ? "active" : ""
                }`}
                onClick={() => setSelectedTab("customRules")}
              >
                <Typography variant="body1">
                  {t("contestacaoDossie.cellParams.menuRules")}
                </Typography>
              </Box>
              <Box
                className={`${classes.sidebarItem} ${
                  selectedTab === "thesisBank" ? "active" : ""
                }`}
                onClick={() => setSelectedTab("thesisBank")}
              >
                <Typography variant="body1">
                  {t("contestacaoDossie.cellParams.menuThesis")}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box
                className={`${classes.sidebarItem} cell-params-sidebar-highlight ${
                  selectedTab === "generationMode" ? "active" : ""
                }`}
                onClick={() => setSelectedTab("generationMode")}
              >
                <Typography variant="body1">
                  {t("contestacao.cellParams.menu.generationMode")}
                </Typography>
              </Box>
              <Box
                className={`${classes.sidebarItem} ${
                  selectedTab === "parameters" ? "active" : ""
                }`}
                onClick={() => setSelectedTab("parameters")}
              >
                <Typography variant="body1">
                  {t("contestacao.cellParams.menu.parameters")}
                </Typography>
              </Box>
              <Box
                className={`${classes.sidebarItem} ${
                  selectedTab === "customRules" ? "active" : ""
                }`}
                onClick={() => setSelectedTab("customRules")}
              >
                <Typography variant="body1">
                  {t("contestacao.cellParams.menu.customRules")}
                </Typography>
              </Box>
              <Box
                className={`${classes.sidebarItem} ${
                  selectedTab === "template" ? "active" : ""
                }`}
                onClick={() => setSelectedTab("template")}
              >
                <Typography variant="body1">
                  {t("contestacao.cellParams.menu.template")}
                </Typography>
              </Box>
              <Box
                className={`${classes.sidebarItem} ${
                  selectedTab === "thesisBank" ? "active" : ""
                }`}
                onClick={() => setSelectedTab("thesisBank")}
              >
                <Typography variant="body1">
                  {t("contestacao.cellParams.menu.thesisBank")}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Área de conteúdo */}
        <Box className={classes.contentArea} style={{ position: "relative" }}>
          {renderContent()}

          {loading && (
            <Box className="cell-loading-overlay">
              <Box className="cell-loading-content">
                <Box className="cell-loading-spinner" />
                {loadError ? (
                  <>
                    <Typography className="cell-loading-retry-text">
                      {t("contestacao.cellParams.loading.retrying", {
                        seconds: retryCountdown,
                      })}
                    </Typography>
                    <Button
                      className="cell-loading-cancel-btn"
                      onClick={handleCancelLoad}
                    >
                      {t("contestacao.cellParams.loading.cancel")}
                    </Button>
                  </>
                ) : (
                  <Typography className="cell-loading-text">
                    {t("contestacao.cellParams.loading.loadingData")}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {/* Apenas botão fechar */}
        <Button
          onClick={onClose}
          variant="outlined"
          className={classes.closeButton}
          disabled={saving || templateUploading}
        >
          {t("contestacao.cellParams.close")}
        </Button>
      </DialogActions>

      {/* Modal fullscreen para textarea expandido */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        fullScreen
        className={classes.fullscreenDialog}
      >
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Box className={classes.titleText}>
            <Typography variant="h6">
              {fullscreenField === "client" &&
                (isDossie
                  ? t("contestacaoDossieCreate.customRules.client")
                  : t("contestacao.cellParams.customRules.client"))}
              {fullscreenField === "internal" &&
                (isDossie
                  ? t("contestacaoDossieCreate.customRules.internal")
                  : t("contestacao.cellParams.customRules.internal"))}
              {fullscreenField === "subsidios" &&
                (isDossie
                  ? t("contestacaoDossieCreate.parameters.subsidiosRules")
                  : "Regras para Subsídios")}
            </Typography>
          </Box>
          <Button
            className={classes.titleCloseButton}
            onClick={handleCloseFullscreen}
            size="small"
          >
            <FullscreenExit />
          </Button>
        </DialogTitle>
        <DialogContent className={classes.fullscreenContent}>
          <TextField
            fullWidth
            multiline
            value={fullscreenValue}
            onChange={(e) => setFullscreenValue(e.target.value)}
            className={classes.fullscreenTextArea}
            variant="outlined"
            placeholder={
              fullscreenField === "client"
                ? isDossie
                  ? t("contestacaoDossieCreate.customRules.clientPlaceholder")
                  : t("contestacao.cellParams.customRules.clientPlaceholder")
                : fullscreenField === "internal"
                  ? isDossie
                    ? t("contestacaoDossieCreate.customRules.internalPlaceholder")
                    : t(
                        "contestacao.cellParams.customRules.internalPlaceholder",
                      )
                  : isDossie
                    ? t(
                        "contestacaoDossieCreate.parameters.subsidiosRulesPlaceholder",
                      )
                    : "Regras específicas para análise de subsídios na criação da contestação..."
            }
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleSaveAndCloseFullscreen}
            variant="contained"
            className={classes.saveButton}
            disabled={saving}
          >
            {saving
              ? tCommon("common.saving")
              : t("contestacao.cellParams.saveAndClose")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
    {/* Modal de importação individual de tese (DC_TESE_IMPORT). Mantido
        como sibling do Dialog principal para que o backdrop dele apareça
        sobre o CellParamsModal e o histórico atrás continue legível ao
        fechar. Após criação do job, dispara refresh no histórico. */}
    <TeseImportModal
      open={teseImportModalOpen}
      onClose={() => setTeseImportModalOpen(false)}
      cellId={cellId}
      onJobCreated={() => {
        if (teseImportsHistoryRef.current) {
          teseImportsHistoryRef.current.refresh();
        }
      }}
    />
    </>
  );
};

export default CellParamsModal;

/*
EXEMPLO DE USO EXTERNO (sem botões de salvar):

const [customizationParams, setCustomizationParams] = useState({
  creativity: 2,
  tone: 2,
  scope_topics: 2,
  complexity: 2,
  foundation_depth: 2,
});

const [observations, setObservations] = useState({
  client: '',
  internal: '',
});

<CellParamsModal
  open={modalOpen}
  onClose={() => setModalOpen(false)}
  cellId={null} // Não precisa de cellId para modo externo
  showSaveButtons={false} // Oculta botões de salvar
  customizationParams={customizationParams}
  observations={observations}
  onParamsChange={setCustomizationParams}
  onObservationsChange={setObservations}
/>

// Os valores ficam sincronizados nos estados externos
// e podem ser usados na chamada da API
*/
